/**
 * UIUpdateScheduler - Batched RAF-based UI updates
 *
 * Replaces multiple independent setInterval UI updates with single batched
 * RAF callback, reducing timer overhead and ensuring consistent update timing.
 *
 * Part of TMR-02 requirement from Phase 6 timer consolidation.
 *
 * This scheduler is PASSIVE - it expects to be called via `tick()` from
 * GameLoop or main.js animate(). It does NOT own its own RAF loop.
 *
 * Usage:
 *   import { UIUpdateScheduler, getUIUpdateScheduler } from './systems/UIUpdateScheduler.js';
 *
 *   // Create instance (typically done in main.js)
 *   const scheduler = new UIUpdateScheduler();
 *
 *   // Or use singleton pattern
 *   const scheduler = getUIUpdateScheduler();
 *
 *   // Register UI update callback
 *   scheduler.register('friendBadge', updateFriendBadge, { interval: 250 });
 *
 *   // Call each frame from animate loop
 *   scheduler.tick(deltaTime);
 *
 *   // Cleanup
 *   scheduler.unregister('friendBadge');
 *   scheduler.dispose();
 */

import { RENDERING } from '../config/constants.js';

export class UIUpdateScheduler {
    constructor() {
        /**
         * Map of registered callbacks
         * @type {Map<string, { callback: Function, interval: number, priority: number, lastUpdateTime: number }>}
         */
        this.registrations = new Map();

        /**
         * Accumulated time since scheduler was created (for internal tracking)
         * @type {number}
         */
        this.elapsedTime = 0;
    }

    /**
     * Register a UI update callback
     * @param {string} id - Unique identifier for this callback
     * @param {Function} callback - Function to call on update
     * @param {Object} [options] - Registration options
     * @param {number} [options.interval] - Update interval in ms (default: RENDERING.UI_UPDATE_INTERVAL)
     * @param {number} [options.priority] - Priority for ordering (lower = earlier, default: 0)
     * @returns {boolean} True if registered, false if id already exists
     */
    register(id, callback, options = {}) {
        if (this.registrations.has(id)) {
            return false;
        }

        const interval = options.interval ?? RENDERING.UI_UPDATE_INTERVAL;
        const priority = options.priority ?? 0;

        this.registrations.set(id, {
            callback,
            interval,
            priority,
            lastUpdateTime: 0
        });

        return true;
    }

    /**
     * Unregister a callback by id
     * @param {string} id - The callback id to remove
     * @returns {boolean} True if removed, false if not found
     */
    unregister(id) {
        return this.registrations.delete(id);
    }

    /**
     * Tick the scheduler - called each frame
     * Runs callbacks whose interval has elapsed
     * @param {number} deltaTime - Time since last tick in seconds
     */
    tick(deltaTime) {
        // Convert deltaTime to milliseconds and accumulate
        const deltaMs = deltaTime * 1000;
        this.elapsedTime += deltaMs;

        // Check each registration
        for (const [id, registration] of this.registrations) {
            const timeSinceLastUpdate = this.elapsedTime - registration.lastUpdateTime;

            if (timeSinceLastUpdate >= registration.interval) {
                try {
                    registration.callback(timeSinceLastUpdate);
                } catch (error) {
                    console.error(`[UIUpdateScheduler] Error in callback '${id}':`, error);
                }
                registration.lastUpdateTime = this.elapsedTime;
            }
        }
    }

    /**
     * Get the number of registered callbacks
     * @returns {number}
     */
    getRegistrationCount() {
        return this.registrations.size;
    }

    /**
     * Check if a callback is registered
     * @param {string} id - The callback id to check
     * @returns {boolean}
     */
    hasRegistration(id) {
        return this.registrations.has(id);
    }

    /**
     * Clear all registrations
     */
    dispose() {
        this.registrations.clear();
        this.elapsedTime = 0;
    }

    /**
     * Reset the scheduler (for testing) - alias for dispose
     */
    reset() {
        this.dispose();
    }
}

// =============================================================================
// SINGLETON PATTERN
// =============================================================================

let _instance = null;

/**
 * Get or create UIUpdateScheduler singleton
 * @returns {UIUpdateScheduler} UIUpdateScheduler instance
 */
export function getUIUpdateScheduler() {
    if (!_instance) {
        _instance = new UIUpdateScheduler();
    }
    return _instance;
}

/**
 * Get the current singleton instance without creating
 * @returns {UIUpdateScheduler|null}
 */
export function uiUpdateScheduler() {
    return _instance;
}

/**
 * Reset singleton instance (for testing)
 */
export function resetUIUpdateScheduler() {
    if (_instance) {
        _instance.dispose();
    }
    _instance = null;
}
